import { useState } from "react";
import { T } from "../../constants/theme";
import { useExploreProducts } from "../../hooks/useExploreProducts";
import ExploreHeader from "../../components/explore/ExploreHeader";
import AISearchCard from "../../components/explore/AISearchCard";
import SearchBar from "../../components/explore/SearchBar";
import FilterSidebar from "../../components/explore/FilterSidebar";
import SortDropdown from "../../components/explore/SortDropdown";
import ProductGrid from "../../components/explore/ProductGrid";
import EmptyState from "../../components/explore/EmptyState";
import LoadingSkeleton from "../../components/explore/LoadingSkeleton";

export default function ExplorePage() {
  const {
    categories,
    brands,
    products,
    total,
    page,
    totalPages,
    loading,
    isAiMode,
    stats,
    filters,
    setCategory,
    setBrand,
    setSearch,
    setSort,
    setPage: setProductPage,
    setPriceMin,
    setPriceMax,
    setInStockOnly,
    setSaleOnly,
    setFeaturedOnly,
    runAiSearch,
    clearAiSearch,
    resetFilters,
  } = useExploreProducts();

  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <div style={{ background: T.paper, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        .explore-page { font-family: 'DM Sans', sans-serif; }
        .explore-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }
        @media (max-width: 1100px) {
          .explore-grid { grid-template-columns: repeat(2, 1fr); }
          .explore-filters-desktop { display: none !important; }
          .explore-filters-toggle { display: inline-flex !important; }
          .explore-layout { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .explore-grid { grid-template-columns: 1fr; }
        }
        .explore-shimmer {
          position: relative;
          overflow: hidden;
        }
        .explore-shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
          animation: exploreShimmer 1.4s infinite;
        }
        @keyframes exploreShimmer {
          from { transform: translateX(-100%); }
          to { transform: translateX(100%); }
        }
        .explore-filters-toggle { display: none; }
      `}</style>

      <div className="explore-page">
        <ExploreHeader stats={stats} />

        <AISearchCard onSearch={runAiSearch} loading={isAiMode && loading} />

        <SearchBar value={filters.search} onChange={setSearch} />

        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 20px 80px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 20,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 14, color: T.inkSoft, fontWeight: 600 }}>
              {isAiMode ? `AI Results (${total})` : `${total} Products`}
              {isAiMode && (
                <button
                  onClick={clearAiSearch}
                  style={{
                    marginLeft: 10,
                    border: "none",
                    background: "transparent",
                    color: T.gold,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Clear AI Search ✕
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                className="explore-filters-toggle"
                onClick={() => setFiltersOpen(true)}
                style={{
                  padding: "12px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  background: T.white,
                  cursor: "pointer",
                }}
              >
                Filters
              </button>
              <SortDropdown value={filters.sort} onChange={setSort} />
            </div>
          </div>

          <div className="explore-layout" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 28 }}>
            <div className="explore-filters-desktop">
              <FilterSidebar
                categories={categories}
                brands={brands}
                filters={filters}
                setCategory={setCategory}
                setBrand={setBrand}
                setPriceMin={setPriceMin}
                setPriceMax={setPriceMax}
                setInStockOnly={setInStockOnly}
                setSaleOnly={setSaleOnly}
                setFeaturedOnly={setFeaturedOnly}
                onReset={resetFilters}
              />
            </div>

            <div>
              {loading ? (
                <LoadingSkeleton />
              ) : products.length === 0 ? (
                <EmptyState onReset={resetFilters} />
              ) : (
                <ProductGrid products={products} />
              )}

              {!loading && products.length > 0 && totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 8,
                    marginTop: 36,
                    flexWrap: "wrap",
                  }}
                >
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const n = i + 1;
                    return (
                      <button
                        key={n}
                        onClick={() => setProductPage(n)}
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 8,
                          border: `1px solid ${T.border}`,
                          background: n === page ? T.ink : T.white,
                          color: n === page ? T.white : T.ink,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div
          onClick={() => setFiltersOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.4)",
            zIndex: 1400,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(340px, 90vw)",
              height: "100%",
              background: T.paper,
              padding: 20,
              overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <button
                onClick={() => setFiltersOpen(false)}
                style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer" }}
              >
                ✕
              </button>
            </div>
            <FilterSidebar
              categories={categories}
              brands={brands}
              filters={filters}
              setCategory={setCategory}
              setBrand={setBrand}
              setPriceMin={setPriceMin}
              setPriceMax={setPriceMax}
              setInStockOnly={setInStockOnly}
              setSaleOnly={setSaleOnly}
              setFeaturedOnly={setFeaturedOnly}
              onReset={resetFilters}
            />
          </div>
        </div>
      )}
    </div>
  );
}
